import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAtomValue } from 'jotai/utils';
import { useQueryClient } from 'react-query';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';
import {
  chatContextFiltersAtom,
  dashboardFiltersAtom,
} from 'src/shared/dashboardFilters';
import { chatAgentModeAtom, effectiveChatActionModeAtom } from 'src/shared/chatPreferences';
import { assetContentTypesAtom } from 'src/shared/chat';
import type { ChatContext } from './types';

function routeToPage(pathname: string): string {
  // The asset route is singular (/asset/:id); the plural form is kept for
  // older links. Without both, the agent is told currentPage: 'other' while
  // the user is looking at an asset.
  if (pathname.match(/^\/assets?\/[^/]+/)) return 'asset-detail';
  if (pathname === '/assets') return 'assets';
  if (pathname.match(/^\/collection\/[^/]+/)) return 'collection-detail';
  if (pathname === '/collections') return 'collections';
  if (pathname.match(/^\/shares\/[^/]+/)) return 'share-detail';
  if (pathname === '/shares') return 'shares';
  if (pathname.startsWith('/usage')) return 'usage';
  if (pathname.startsWith('/account') || pathname.startsWith('/admin')) return 'account';
  return 'other';
}

function extractParam(pathname: string, prefix: string): string | undefined {
  if (pathname.startsWith(prefix)) {
    const rest = pathname.slice(prefix.length);
    const segment = rest.split('/')[0];
    return segment || undefined;
  }
  return undefined;
}

/**
 * The URL the user is actually on. `useLocation()` is not it while the asset
 * modal is open: Router.tsx renders the shell under
 * `<Switch location={background || location}>`, so everything inside
 * AppShell (this panel included) is handed the BACKGROUND location — the
 * agent was told `currentPage: 'assets'` with no `currentAssetId` while the
 * user looked at an asset. The history object is not overridden, so follow
 * it directly.
 */
function useActualLocation() {
  const history = useHistory();
  const [location, setLocation] = useState(history.location);
  useEffect(() => history.listen((next) => setLocation(next)), [history]);
  return location;
}

export function useChatContext(): ChatContext {
  const location = useActualLocation();
  const selectedAssetIds = useAtomValue(selectedAssetsAtom);
  const filterState = useAtomValue(dashboardFiltersAtom);
  const activeFilters = useAtomValue(chatContextFiltersAtom);
  const actionMode = useAtomValue(effectiveChatActionModeAtom);
  const agentMode = useAtomValue(chatAgentModeAtom);
  const queryClient = useQueryClient();

  const knownTypes = useAtomValue(assetContentTypesAtom);
  const selectedAssetTypes = useMemo(() => {
    return selectedAssetIds.map((id) => {
      const cached = queryClient.getQueryData(['asset', id]) as any;
      return cached?.content_type || knownTypes[id] || 'unknown';
    });
  }, [selectedAssetIds, queryClient, knownTypes]);

  // The committed keyword from the dashboard's canonical filter store. The
  // legacy `searchAtom` is still populated by other surfaces (collection /
  // share search bars) but is no longer the source of truth for the assets
  // page. We send the dashboard keyword instead so the chat assistant sees
  // the same query the user is actually filtering by.
  const searchQuery = filterState.keyword || '';

  return useMemo(
    () => ({
      currentRoute: location.pathname,
      currentPage: routeToPage(location.pathname),
      selectedAssetIds,
      selectedAssetTypes,
      selectedCount: selectedAssetIds.length,
      searchQuery,
      activeFilters,
      currentAssetId: extractParam(location.pathname, '/assets/') || extractParam(location.pathname, '/asset/'),
      currentCollectionId: extractParam(location.pathname, '/collection/'),
      currentShareId: extractParam(location.pathname, '/shares/'),
      actionMode,
      agentMode,
    }),
    [
      location.pathname,
      selectedAssetIds,
      selectedAssetTypes,
      searchQuery,
      activeFilters,
      actionMode,
      agentMode,
    ],
  );
}
